<template>
  <b-container
    fluid
    class="px-0 ml-1"
  >
    <b-row
      align-h="between"
    >

      <!-- #region begin::Product details title -->
      <b-col cols="6">
        <QuoteProductTitleDetail
          :id="product.IdProduct"
          :name="product.Name"
          :type="product.Type"
          :count="countState"
          :available="availableProduct"
          :save-route="false"
          :set-pieces-query="setPiecesQuery"
          :product="setPiecesQuery ? product : null"
        />
      </b-col>
      <!-- #endregion end::Product details title -->

      <b-col cols="6">
        <b-container fluid>
          <b-row
            align-h="end"
            align-v="center"
          >

            <b-col md="7">
              <div class="d-flex justify-content-center">
                <span class="align-self-center text-nowrap mr-1">Método de entrega</span>
                <div class="w-100">
                  <SelectWithValidation
                    v-model="selectedDeliveryMethod"
                    :vid="deliveryMethodUUID"
                    name="método de entrega"
                    property="text"
                    placeholder="Selecciona el método de entrega"
                    :options="getDeliveryMethods"
                    :disabled="disabledFormComponents"
                    :clearable="false"
                  />
                </div>
              </div>
            </b-col>

            <!-- #region begin::Delete product button -->
            <b-button
              v-if="showDeleteButton"
              v-b-tooltip.hover.top="'Eliminar'"
              variant="flat-secondary"
              class="btn-icon px-2"
              @click="onDeleteProduct"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
            <!-- #endregion end::Delete product button -->

            <!-- #region begin::Edit product button -->
            <b-button
              v-if="showEditButton"
              v-b-tooltip.hover.top="'Editar'"
              variant="flat-secondary"
              class="btn-icon px-2"
              @click="$refs.updateProductModal.showModal()"
            >
              <feather-icon icon="Edit3Icon" />
            </b-button>
            <!-- #endregion end::Edit product button -->

          </b-row>
        </b-container>
      </b-col>
    </b-row>

    <QuoteProductDetails
      :product="product"
      :disabled-form-components="disabledFormComponents"
      :available="availableProduct"
      :icon-toggle="iconToggle"
      :visible="visible"
      @toggle-collapse="collapse"
    />
    <UpdateProductModal
      v-if="loadEditProductModal"
      :id="product.IdProduct"
      ref="updateProductModal"
    />

  </b-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  BRow, BCol, BContainer, BButton, VBTooltip,
} from 'bootstrap-vue'

import SelectWithValidation from '@/components/forms/SelectWithValidation.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import UpdateProductModal from '@/modules/trade/quotes/components/modals/UpdateProductModal.vue'
import QuoteProductDetails from '@/modules/trade/quotes/components/cards/QuoteProductDetails.vue'
import QuoteProductTitleDetail from '@/modules/trade/quotes/components/cards/QuoteProductTitleDetail.vue'

import { v4 as uuidv4 } from 'uuid'

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BContainer,
    UpdateProductModal,
    QuoteProductDetails,
    SelectWithValidation,
    QuoteProductTitleDetail,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
    collapseItemId: {
      type: String,
      required: true,
    },
    showDeleteButton: {
      type: Boolean,
      default: true,
    },
    showEditButton: {
      type: Boolean,
      default: true,
    },
    loadEditProductModal: {
      type: Boolean,
      default: true,
    },

    // * 22/11/2022 - TAG: For quote details
    disabledFormComponents: {
      type: Boolean,
      default: false,
    },
    setPiecesQuery: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      visible: false,
      iconToggle: 'ChevronDownIcon',
      selectedDeliveryMethod: null,
    }
  },
  computed: {
    ...mapGetters({
      getDeliveryMethods: 'quotes/getDeliveryMethods',
    }),
    /**
     * Cantidad del producto en el inventario
     */
    countState() {
      if (this.$route.name === 'quote-details') {
        return this.product.QtyIventory
      }
      return this.product.Qty
    },
    /**
     * Indica si el producto está disponible para ser agregado a la cotización
     */
    availableProduct() {
      if (this.$route.name !== 'quote-details') {
        return true
      }

      return this.product.Status === 1
    },
    /**
     * UUID para el método de entrega
     */
    deliveryMethodUUID() {
      return uuidv4()
    },
  },
  watch: {
    selectedDeliveryMethod(newDeliveryMethod) {
      this.$set(this.product, 'DeliveredMethod', newDeliveryMethod.text)
    },
  },

  /**
  * Hook que se ejecuta cuando el componente es creado
  *
  * @summary Establece la visibilidad del componente de lista de piezas del producto
  * y el método de entrega seleccionado
  */
  created() {
    this.visible = this.isVisible
    this.setToggleIcon()

    if (this.product.DeliveredMethod) {
      this.selectedDeliveryMethod = { text: this.product.DeliveredMethod }
    }
  },
  methods: {
    ...mapActions({
      addQuoteProduct: 'quotes/addQuoteProduct',
    }),

    /**
     * Colapsar detalles del producto
     *
     * @summary Colapsa el componente de la lista de piezas del producto
     */
    collapse() {
      if (!this.visible) {
        this.$emit('rendering', true)
      }
      this.$root.$emit('bv::toggle::collapse', this.collapseItemId)
      this.visible = !this.visible
      this.setToggleIcon()
    },

    /**
     * Icono para el botón de colapsar
     *
     * @summary Establece el icono para el botón de colapsar
     */
    setToggleIcon() {
      this.iconToggle = this.visible ? 'ChevronUpIcon' : 'ChevronDownIcon'
    },

    /**
    * Eliminación de producto
    *
    * @summary Elimina el producto de la cotización
    */
    onDeleteProduct() {
      const quoteProduct = { ...this.product }
      quoteProduct.pieces = []
      this.addQuoteProduct(quoteProduct)

      this.showToast(
        'Producto eliminado',
        'El producto se ha eliminado correctamente de la cotización',
        'success',
      )
    },

    /**
    * Mensaje de alerta
    *
    * @summary Muestra un mensaje de alerta personalizado
    * @param {string} title - Título del mensaje
    * @param {string} text - Texto del mensaje
    * @param {string} variant - Tipo de mensaje
    */
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>
